import {
  AdCreative,
  AdCreativeType,
  AdImage,
  AdVideo,
  CreativeCopyPatch,
  CreativeMediaPatch,
  CreativeMediaRef,
  CreativeSlideAdd,
} from '@marketing-milk/interfaces'

/**
 * The media half of a review draft: staged operations against the saved ad, plus
 * the assets those operations name so the preview can draw them before a save.
 *
 * The operations live in `CreativeCopyPatch['media']` — the request body itself,
 * exactly as the copy half does — so nothing is mapped on the way out. Only what
 * the request has no room for lives here alongside it: the uploaded file's URLs.
 */

export type UploadedMedia =
  | { mediaType: 'image'; asset: AdImage }
  | { mediaType: 'video'; asset: AdVideo }

/** Uploads by `image:12` / `video:12` — an image and a video can share an id. */
export type UploadedMediaMap = Record<string, UploadedMedia>

export const mediaKey = (ref: CreativeMediaRef) => `${ref.mediaType}:${ref.mediaID}`

/**
 * One asset as everything here draws it — a tile, a pool entry, the preview. One
 * representation rather than one per consumer, so the tiles and the ad beside
 * them cannot disagree about what was just uploaded.
 */
export type DraftMedia =
  | { mediaType: 'image'; mediaID: number; url?: string }
  | {
      mediaType: 'video'
      mediaID: number
      source?: string
      storedSource?: string
      thumbnail?: string
      /** A `.gif` — uploaded as a video, but drawn as an image. See `AdVideo.animatedImage`. */
      animatedImage?: boolean
    }

/** What an upload, once it lands, should be applied to. */
export type MediaUploadTarget =
  | { kind: 'single' }
  | { kind: 'card'; card: DraftCard }
  | { kind: 'add-card' }
  | { kind: 'asset'; replacing: CreativeMediaRef }
  | { kind: 'add-asset' }

/**
 * The media actions an employee gets. A capability rather than a permission
 * flag — the editors never learn what a permission is, and its absence is what
 * makes the tiles read-only.
 */
export interface MediaEditing {
  /** Read-only for a reason the caller states elsewhere (e.g. the ad is published). */
  disabled: boolean
  /** An upload is in flight. One at a time, so every media action waits on it. */
  uploading: boolean
  onUpload: (target: MediaUploadTarget) => void
  onRemoveCard: (card: DraftCard) => void
  onRemoveAsset: (ref: CreativeMediaRef) => void
}

/** One carousel card as the editor shows it: saved, edited, or not yet saved. */
export interface DraftCard {
  /** Stable across a save for a card that survives it. */
  key: string
  /** Absent until the card has been saved. */
  id?: number
  /** Where this card's copy lives in `media.slides.add`, for a pending card. */
  addIndex?: number
  headline: string
  description: string
  url: string
  media?: DraftMedia
}

export const imageMedia = (image: AdImage): DraftMedia => ({
  mediaType: 'image',
  mediaID: image.id,
  url: image.url,
})

export const videoMedia = (video: AdVideo): DraftMedia => ({
  mediaType: 'video',
  mediaID: video.id,
  source: video.source,
  storedSource: video.storedSource,
  thumbnail: video.thumbnail,
  animatedImage: video.animatedImage,
})

const uploadedMedia = (
  uploads: UploadedMediaMap,
  ref: CreativeMediaRef
): DraftMedia | undefined => {
  const upload = uploads[mediaKey(ref)]
  if (!upload) return undefined
  return upload.mediaType === 'image' ? imageMedia(upload.asset) : videoMedia(upload.asset)
}

/**
 * What a tile should draw, and with which element. A video has no still of its
 * own until Facebook has made one, so `video` means "show a frame of this" —
 * see `videoStillURL`.
 */
export type MediaThumb = { kind: 'image'; url: string } | { kind: 'video'; url: string }

/**
 * The picture for one asset's tile.
 *
 * Facebook's thumbnail is preferred where it exists, but it lands minutes after
 * an upload, and a blank tile is worse than our own copy of the very file the
 * reviewer just chose. A GIF is an image at every step here.
 */
export function mediaThumb(media?: DraftMedia): MediaThumb | undefined {
  if (!media) return undefined
  if (media.mediaType === 'image') {
    return media.url ? { kind: 'image', url: media.url } : undefined
  }
  if (media.animatedImage) {
    const url = media.storedSource ?? media.thumbnail
    return url ? { kind: 'image', url } : undefined
  }
  if (media.thumbnail) return { kind: 'image', url: media.thumbnail }
  return media.storedSource ? { kind: 'video', url: media.storedSource } : undefined
}

/** One asset in the shape the local renderer reads a single ad's media in. */
export const previewSingleMedia = (media: DraftMedia) =>
  media.mediaType === 'video'
    ? {
        format: AdCreativeType.single_video,
        imageURL: undefined,
        singleVideo: {
          source: media.source,
          storedSource: media.storedSource,
          thumbnail: media.thumbnail,
          animatedImage: media.animatedImage,
        },
      }
    : { format: AdCreativeType.single_image, imageURL: media.url, singleVideo: undefined }

export function draftSingleMedia(
  creative: AdCreative,
  draft: CreativeCopyPatch,
  uploads: UploadedMediaMap
): DraftMedia | undefined {
  const replacement = draft.media?.single
  if (replacement) return uploadedMedia(uploads, replacement)
  if (creative.singleVideo) return videoMedia(creative.singleVideo)
  if (creative.singleImage) return imageMedia(creative.singleImage)
  return undefined
}

/**
 * The cards on screen: saved ones with their edits and replacements applied,
 * removals dropped, then the ones this draft adds. Derived every render rather
 * than held as a second list, so no copy of the cards can disagree with the
 * request Save sends.
 */
export function draftCards(
  creative: AdCreative,
  draft: CreativeCopyPatch,
  uploads: UploadedMediaMap
): DraftCard[] {
  const slides = draft.media?.slides
  const removed = new Set(slides?.remove ?? [])
  const replacements = new Map((slides?.replace ?? []).map(entry => [entry.id, entry]))
  const copyEdits = new Map((draft.slides ?? []).map(entry => [entry.id, entry]))

  const saved: DraftCard[] = (creative.carouselSlides ?? [])
    .filter(slide => slide.id !== undefined && !removed.has(slide.id))
    .map(slide => {
      const edit = copyEdits.get(slide.id!)
      const replacement = replacements.get(slide.id!)
      const savedMedia =
        slide.mediaType === 'video'
          ? videoMedia(slide.video)
          : slide.mediaType === 'image'
          ? imageMedia(slide.image)
          : undefined
      return {
        key: `saved:${slide.id}`,
        id: slide.id,
        headline: edit?.headline ?? slide.linkHeadline ?? '',
        description: edit?.description ?? slide.linkDescription ?? '',
        url: edit?.url ?? slide.linkWebsiteURL ?? '',
        media: replacement ? uploadedMedia(uploads, replacement) : savedMedia,
      }
    })

  const added: DraftCard[] = (slides?.add ?? []).map((addition, index) => ({
    key: `new:${index}`,
    addIndex: index,
    headline: addition.headline ?? '',
    description: addition.description ?? '',
    url: addition.url ?? '',
    media: uploadedMedia(uploads, addition),
  }))

  return [...saved, ...added]
}

export function draftAssets(
  creative: AdCreative,
  draft: CreativeCopyPatch,
  uploads: UploadedMediaMap
): DraftMedia[] {
  const ops = draft.media?.dynamic
  const removed = new Set((ops?.remove ?? []).map(mediaKey))

  return [
    ...[
      ...(creative.dynamicImages ?? []).map(imageMedia),
      ...(creative.dynamicVideos ?? []).map(videoMedia),
    ].filter(media => !removed.has(mediaKey(media))),
    ...(ops?.add ?? [])
      .map(ref => uploadedMedia(uploads, ref))
      .filter((media): media is DraftMedia => !!media),
  ]
}

/**
 * Drops keys an undo emptied. Undoing a staged change has to leave a draft equal
 * to the one it started from — a leftover `add: []` differs from an untouched
 * draft, which would block approval and warn about discarding nothing.
 */
function compact<T extends object>(value: T): T | undefined {
  const kept = Object.entries(value).filter(
    ([, entry]) => entry !== undefined && !(Array.isArray(entry) && entry.length === 0)
  )
  return kept.length ? (Object.fromEntries(kept) as T) : undefined
}

function withMedia(draft: CreativeCopyPatch, media: CreativeMediaPatch): CreativeCopyPatch {
  const next = compact({
    ...media,
    slides: media.slides && compact(media.slides),
    dynamic: media.dynamic && compact(media.dynamic),
  })
  if (next) return { ...draft, media: next }
  const { media: _dropped, ...rest } = draft
  return rest
}

type SlideOps = NonNullable<CreativeMediaPatch['slides']>
type DynamicOps = NonNullable<CreativeMediaPatch['dynamic']>

const withSlides = (draft: CreativeCopyPatch, slides: SlideOps): CreativeCopyPatch =>
  withMedia(draft, { ...draft.media, slides: { ...draft.media?.slides, ...slides } })

const withDynamic = (draft: CreativeCopyPatch, dynamic: DynamicOps): CreativeCopyPatch =>
  withMedia(draft, { ...draft.media, dynamic: { ...draft.media?.dynamic, ...dynamic } })

const additions = (draft: CreativeCopyPatch) => draft.media?.slides?.add ?? []

/**
 * A pending card's media and its copy both live in the addition itself — the card
 * does not exist yet to have either replaced.
 */
const withPendingCard = (
  draft: CreativeCopyPatch,
  index: number,
  changes: Partial<CreativeSlideAdd>
): CreativeCopyPatch =>
  withSlides(draft, {
    add: additions(draft).map((entry, i) => (i === index ? { ...entry, ...changes } : entry)),
  })

export const withSingleMedia = (
  draft: CreativeCopyPatch,
  ref: CreativeMediaRef
): CreativeCopyPatch => withMedia(draft, { ...draft.media, single: ref })

export function withCardMedia(
  draft: CreativeCopyPatch,
  card: DraftCard,
  ref: CreativeMediaRef
): CreativeCopyPatch {
  if (card.addIndex !== undefined) return withPendingCard(draft, card.addIndex, ref)
  return withSlides(draft, {
    replace: [
      ...(draft.media?.slides?.replace ?? []).filter(entry => entry.id !== card.id),
      { id: card.id!, ...ref },
    ],
  })
}

export const withCardAdded = (draft: CreativeCopyPatch, ref: CreativeMediaRef): CreativeCopyPatch =>
  withSlides(draft, { add: [...additions(draft), ref] })

/** Removing a card that was never saved just drops the instruction to add it. */
export function withCardRemoved(draft: CreativeCopyPatch, card: DraftCard): CreativeCopyPatch {
  if (card.addIndex !== undefined) {
    return withSlides(draft, { add: additions(draft).filter((_, i) => i !== card.addIndex) })
  }
  const slides = draft.media?.slides
  // A card being deleted carries no copy edit and no replacement — the server
  // refuses both, and they would describe a card that no longer exists.
  return withSlides(
    { ...draft, slides: (draft.slides ?? []).filter(entry => entry.id !== card.id) },
    {
      replace: (slides?.replace ?? []).filter(entry => entry.id !== card.id),
      remove: [...(slides?.remove ?? []), card.id!],
    }
  )
}

/** The copy edit for one card, routed to wherever that card's copy lives. */
export function withCardCopy(
  draft: CreativeCopyPatch,
  card: DraftCard,
  changes: { headline?: string; description?: string; url?: string }
): CreativeCopyPatch {
  if (card.addIndex !== undefined) return withPendingCard(draft, card.addIndex, changes)
  return {
    ...draft,
    slides: (draft.slides ?? []).map(entry =>
      entry.id === card.id ? { ...entry, ...changes } : entry
    ),
  }
}

export const withAssetAdded = (
  draft: CreativeCopyPatch,
  ref: CreativeMediaRef
): CreativeCopyPatch => withDynamic(draft, { add: [...(draft.media?.dynamic?.add ?? []), ref] })

/** Removing an asset this draft added just drops the addition. */
export function withAssetRemoved(
  draft: CreativeCopyPatch,
  ref: CreativeMediaRef
): CreativeCopyPatch {
  const dynamic = draft.media?.dynamic
  const key = mediaKey(ref)
  if ((dynamic?.add ?? []).some(entry => mediaKey(entry) === key)) {
    return withDynamic(draft, { add: (dynamic?.add ?? []).filter(e => mediaKey(e) !== key) })
  }
  return withDynamic(draft, { remove: [...(dynamic?.remove ?? []), ref] })
}
